import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface PriceAlert {
  id: string;
  assetId: string;
  assetSymbol: string;
  threshold: number;
  direction: 'above' | 'below';
  enabled: boolean;
  triggered: boolean;
  triggeredAt?: string;
}

interface AlertState {
  alerts: PriceAlert[];
  addAlert: (alert: Omit<PriceAlert, 'id' | 'triggered'>) => void;
  removeAlert: (id: string) => void;
  toggleAlert: (id: string) => void;
  checkAlerts: (assetId: string, currentPrice: number) => PriceAlert[];
  markTriggered: (id: string) => void;
}

export const useAlertStore = create<AlertState>()(
  persist(
    (set, get) => ({
      alerts: [],
      addAlert: (alert) => {
        const newAlert: PriceAlert = {
          ...alert,
          id: `alert-${Date.now()}`,
          triggered: false,
        };
        set((state) => ({
          alerts: [...state.alerts, newAlert],
        }));
      },
      removeAlert: (id) =>
        set((state) => ({
          alerts: state.alerts.filter((a) => a.id !== id),
        })),
      toggleAlert: (id) =>
        set((state) => ({
          alerts: state.alerts.map((a) =>
            a.id === id ? { ...a, enabled: !a.enabled } : a
          ),
        })),
      checkAlerts: (assetId, currentPrice) => {
        const { alerts } = get();
        const triggeredAlerts: PriceAlert[] = [];

        alerts.forEach((alert) => {
          if (
            alert.assetId === assetId &&
            alert.enabled &&
            !alert.triggered
          ) {
            const shouldTrigger =
              alert.direction === 'above'
                ? currentPrice >= alert.threshold
                : currentPrice <= alert.threshold;

            if (shouldTrigger) {
              triggeredAlerts.push(alert);
              get().markTriggered(alert.id);
            }
          }
        });

        return triggeredAlerts;
      },
      markTriggered: (id) =>
        set((state) => ({
          alerts: state.alerts.map((a) =>
            a.id === id
              ? { ...a, triggered: true, triggeredAt: new Date().toISOString() }
              : a
          ),
        })),
    }),
    {
      name: 'alert-storage',
    }
  )
);

